import React, { useState, useEffect } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, Button, Stack, CircularProgress, Alert
} from "@mui/material";
import axios from "axios";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-python";
import "ace-builds/src-noconflict/theme-dracula";
import "ace-builds/src-noconflict/snippets/python";

const CreateConnectorDialog = ({ brokerId, open, onClose, mode = "create", initialData = null }) => {
    const [broker, setBroker] = useState(null);
    const [formData, setFormData] = useState({});
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(true);
    const [status, setStatus] = useState({ type: "", message: "" });
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;


    useEffect(() => {
        if (open && brokerId) {
            setStatus({ type: "", message: "" });
            const fetchBroker = async () => {
                try {
                    const token = localStorage.getItem("token");
                    const res = await axios.get(`${API_BASE_URL}/brokers/broker/${brokerId}`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setBroker(res.data);
                    if (initialData) {
                        setFormData({
                            connector_name: initialData.connector_name,
                            connector_code: initialData.connector_code,
                            ...initialData.attributes
                        });
                    }
                    setLoading(false);
                } catch (err) {
                    console.error("Error loading broker", err);
                }
            };
            fetchBroker();
        }
    }, [open, brokerId]);

    const handleChange = (varName, value) => {
        setFormData({ ...formData, [varName]: value });
    };

    const validate = () => {
        const errs = {};
        // Validate connector_name
        if (!formData["connector_name"]) {
            errs["connector_name"] = "Connector Name is required";
        }

        broker.attributes.forEach(attr => {
            const value = formData[attr.varName];
            if (attr.required && !value) {
                errs[attr.varName] = "Required";
            } else if (attr.dataType === "int" && isNaN(parseInt(value))) {
                errs[attr.varName] = "Must be an integer";
            } else if (attr.dataType === "str" && typeof value !== "string") {
                errs[attr.varName] = "Must be a string";
            }
        });
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;
        try {
            const token = localStorage.getItem("token");
            const { connector_name, connector_code, ...attributeFields } = formData;

            if (mode === "edit") {
                await axios.patch(
                    `${API_BASE_URL}/user-connector/my/${initialData._id}`,
                    {
                        connector_name,
                        connector_code,
                        attributes: attributeFields
                    },
                    { headers: { Authorization: `Bearer ${token}` } }
                );
            } else {
                await axios.post(`${API_BASE_URL}/user-connector`, {
                    connector_name,
                    connector_code,
                    broker_id: brokerId,
                    attributes: attributeFields,
                }, {
                    headers: { Authorization: `Bearer ${token}` }
                });
            }
            setStatus({ type: "success", message: "Connector created successfully!" });
            setTimeout(() => onClose(true), 1500);
        } catch (err) {
            console.error("Error saving connector", err);
            setStatus({
                type: "error",
                message: err.response?.data?.detail || "Failed to save",
            });
        }
    };

    if (!open) return null;

    return (
        <Dialog open={open} onClose={() => onClose(false)} fullWidth maxWidth="sm">
            <DialogTitle>Create Connector</DialogTitle>
            <DialogContent dividers>
                {status.message && (
                    <Alert severity={status.type} sx={{ mb: 2 }}>
                        {status.message}
                    </Alert>
                )}
                {loading ? <CircularProgress /> : (
                    <Stack spacing={2} mt={1}>
                        <TextField
                            key="connector_name"
                            label="Connector Name"
                            value={formData["connector_name"] || ""}
                            onChange={(e) => handleChange("connector_name", e.target.value)}
                            required
                            error={!!errors["connector_name"]}
                            helperText={errors["connector_name"] || ""}
                        />
                        <Stack spacing={1}>
                            <label style={{ fontWeight: 500 }}>Connector Code</label>
                            <div style={{ border: '1px solid #ccc', borderRadius: '4px', overflow: 'hidden' }}>
                                <AceEditor
                                    mode="python"
                                    theme="dracula"
                                    name="connector_code"
                                    value={formData["connector_code"] || ""}
                                    onChange={(value) => handleChange("connector_code", value)}
                                    fontSize={14}
                                    width="100%"
                                    height="300px"
                                    style={{
                                        backgroundColor: "#2f3136",
                                        color: "#f8f8f2",
                                        borderRadius: "4px"
                                    }}
                                    setOptions={{
                                        useWorker: true,
                                        tabSize: 4,
                                        enableSnippets: true,
                                        showLineNumbers: true,
                                        highlightActiveLine: true,
                                        enableBasicAutocompletion: true,
                                        enableLiveAutocompletion: true
                                    }}
                                />
                            </div>
                            {errors["connector_code"] && (
                                <span style={{ color: "#d32f2f", fontSize: "0.75rem" }}>
                                    {errors["connector_code"]}
                                </span>
                            )}
                        </Stack>
                        {broker.attributes.map((attr) => (
                            <TextField
                                key={attr.varName}
                                label={attr.label}
                                value={formData[attr.varName] || ""}
                                onChange={(e) => handleChange(attr.varName, e.target.value)}
                                required={attr.required}
                                error={!!errors[attr.varName]}
                                helperText={errors[attr.varName] || ""}
                            />
                        ))}
                    </Stack>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose(false)} color="secondary">Cancel</Button>
                <Button onClick={handleSubmit} variant="contained">Submit</Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateConnectorDialog;
